import { useEffect, useState, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import FleetMap from "@/components/FleetMap";
import api from "@/lib/api";
import { businessStatus, alertMeta, timeAgo, fmtTime, getVehicleIcon, fmtSpeed, isMarine, staleThresholdMs } from "@/lib/fleet";
import { InvoiceModal } from "@/components/adminkit";
import UnitControlModal from "@/components/UnitControlModal";
import EngineCutDialog from "@/components/EngineCutDialog";
import TripsModal from "@/components/TripsModal";
import SensorChartModal from "@/components/SensorChartModal";
import FuelChartModal from "@/components/FuelChartModal";
import RouteToolPanel from "@/components/RouteToolPanel";
import { AddressLabel } from "@/components/AddressLabel";
import { reverseGeocode } from "@/lib/geocode";
import FleetList from "@/components/FleetList";
import VehicleDrawer from "@/components/VehicleDrawer";
import NavigationOverlay from "@/components/NavigationOverlay";
import { Maximize2, Minimize2, LocateFixed, Navigation as RouteArrow, Link2, Siren } from "lucide-react";
import HistoryModal from "@/components/HistoryModal";
import PoiPanel from "@/components/PoiPanel";
import DevicesStatusModal from "@/components/DevicesStatusModal";
import BrandLoader from "@/components/BrandLoader";
import { Truck, Gauge, Navigation, Search, X, Bell, History, Play, Pause, RotateCcw, Square, Zap, BatteryMedium, Satellite, SignalHigh, Info, LayoutList, Share2, Copy, MapPin, Route, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, FileText, Terminal, UserRound, Download, Crosshair, FileJson, FileSpreadsheet, Power, Flag, Clock, Wrench, Leaf, Settings, AlertTriangle } from "lucide-react";
import { canEntries } from "@/lib/can";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const battPct = (mv) => {
  if (mv == null) return null;
  const v = mv / 1000;
  return Math.max(0, Math.min(100, Math.round(((v - 3.3) / (4.2 - 3.3)) * 100)));
};

const METRIC_COLORS = { voltage: "#17A2B8", battery: "#10B981", satellites: "#F5821F", gsm: "#A855F7" };

const ecoColor = (s) => (s == null ? "#71717A" : s >= 80 ? "#10B981" : s >= 60 ? "#F59E0B" : "#EF4444");

const SensorBubble = ({ icon: Icon, value, color, testid, title, onClick, onEnter, onLeave }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    data-testid={testid}
    title={title}
    className="flex items-center gap-1 rounded-full px-2 py-0.5 data text-[11px] font-semibold border transition-transform hover:scale-105"
    style={{ color, background: `${color}1f`, borderColor: `${color}55` }}
  >
    <Icon className="w-3 h-3" /> {value}
  </button>
);

const fmtDur = (min) => {
  if (min == null) return "—";
  if (min < 60) return `${min}min`;
  return `${Math.floor(min / 60)}h${String(min % 60).padStart(2, "0")}`;
};
const firstTripHour = (isoStr) => {
  if (!isoStr) return "—";
  try { return new Date(isoStr).toLocaleTimeString("fr-FR", { timeZone: "Pacific/Noumea", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

const Sensor = ({ icon: Icon, value, color, testid, title }) => (
  <span
    className="flex items-center gap-1 rounded-full px-2 py-0.5 data text-[11px] font-semibold border"
    style={{ color: color || "#a1a1aa", background: `${color || "#a1a1aa"}1f`, borderColor: `${color || "#a1a1aa"}55` }}
    data-testid={testid}
    title={title}
  >
    <Icon className="w-3 h-3" /> {value}
  </span>
);



const SPEEDS = [1, 2, 4, 8];

// --- Détection « véhicule dans une zone POI » (miroir de point_in_poi backend + tronçon) ---
const _haversineM = (aLat, aLon, bLat, bLon) => {
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLon = toRad(bLon - aLon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};
const _pointInPolygon = (lat, lon, coords) => {
  let inside = false;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i].lon, yi = coords[i].lat, xj = coords[j].lon, yj = coords[j].lat;
    if (((yi > lat) !== (yj > lat)) && (lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
};
// distance point → segment en mètres (approx. plane locale)
const _distToSegM = (lat, lon, a, b) => {
  const mPerDegLat = 111320, mPerDegLon = 111320 * Math.cos((lat * Math.PI) / 180);
  const px = (lon - a.lon) * mPerDegLon, py = (lat - a.lat) * mPerDegLat;
  const bx = (b.lon - a.lon) * mPerDegLon, by = (b.lat - a.lat) * mPerDegLat;
  const len2 = bx * bx + by * by;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / len2));
  const dx = px - t * bx, dy = py - t * by;
  return Math.sqrt(dx * dx + dy * dy);
};
const poiContains = (lat, lon, p) => {
  if (lat == null || lon == null) return false;
  if (p.type === "polygon" && (p.coordinates || []).length >= 3) return _pointInPolygon(lat, lon, p.coordinates);
  if (p.type === "troncon" && (p.coordinates || []).length >= 2) {
    const half = Math.max(5, (p.width_m || 30) / 2);
    for (let i = 1; i < p.coordinates.length; i++) {
      if (_distToSegM(lat, lon, p.coordinates[i - 1], p.coordinates[i]) <= half) return true;
    }
    return false;
  }
  if (p.lat != null && p.radius > 0) return _haversineM(lat, lon, p.lat, p.lon) <= p.radius;
  return false;
};

const usageText = (v) => (v?.usage_metric === "engine_hours"
  ? `${Math.round(v.engine_hours || 0).toLocaleString("fr-FR")} h`
  : `${Math.round(v?.odometer_km || 0).toLocaleString("fr-FR")} km`);

// P0.2.5 / SLEEP MODE — statut métier « depuis X ». « offline » n'est PLUS présenté comme panne :
// on retombe toujours sur le dernier mouvement connu (À l'arrêt / En mouvement / Au ralenti depuis X).
const STATUS_VERB = { moving: "En mouvement depuis", idling: "Au ralenti depuis", stopped: "À l'arrêt depuis" };
const humanDur = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), mm = m % 60;
  if (h < 24) return `${h} h${mm ? ` ${mm}` : ""}`;
  const d = Math.floor(h / 24); return `${d} j ${h % 24} h`;
};
const statusSince = (v) => {
  const now = Date.now();
  const raw = v?.status;
  const st = (raw === "moving" || raw === "idling" || raw === "stopped") ? raw : "stopped";
  const since = v?.status_changed_at ? Date.parse(v.status_changed_at) : (v?.last_position?.timestamp ? Date.parse(v.last_position.timestamp) : null);
  const gpsTs = v?.last_position?.timestamp ? Date.parse(v.last_position.timestamp) : (v?.last_gps_at ? Date.parse(v.last_gps_at) : null);
  const fresh = gpsTs != null && (now - gpsTs) <= staleThresholdMs(v, st);
  return { label: STATUS_VERB[st], dur: since ? humanDur(now - since) : "—", off: false, moving: st === "moving", fresh, gpsTs };
};


export default function Dashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [geofences, setGeofences] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [canSettings, setCanSettings] = useState(false);
  const [track, setTrack] = useState([]);
  const [q, setQ] = useState("");
  const [playback, setPlayback] = useState(null);
  const [mobilePanel, setMobilePanel] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [compact, setCompact] = useState(false);
  const [share, setShare] = useState(null);
  const [addresses, setAddresses] = useState({});
  const [currentTrip, setCurrentTrip] = useState(null);
  const [alertsCollapsed, setAlertsCollapsed] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [companies, setCompanies] = useState([]);
  const { user, can } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManagePoi = can("pois_manage");
  const canImmobilize = can("immobilizer");
  const defaultPoiCompany = () => (companyFilters.length === 1 ? companyFilters[0] : (companies.length === 1 ? companies[0].id : ""));
  const [companyFilters, setCompanyFilters] = useState([]);  // P0.2.5 — multi-sélection sociétés ([] = toutes)
  const [pools, setPools] = useState([]);
  const [poolFilter, setPoolFilter] = useState("");
  const [vehFilter, setVehFilter] = useState([]);
  const [vehOpen, setVehOpen] = useState(false);
  const [showControl, setShowControl] = useState(false);
  const [showTrips, setShowTrips] = useState(false);
  const [sensorChart, setSensorChart] = useState(null);
  const [drawerTab, setDrawerTab] = useState("resume");   // LT1.2 : onglet actif de la fiche véhicule
  const [fuelChart, setFuelChart] = useState(null);
  const [dayTrips, setDayTrips] = useState(null);
  const [tripAddr, setTripAddr] = useState({});
  const [cutDialog, setCutDialog] = useState(null);
  const [pois, setPois] = useState([]);
  const [poiImport, setPoiImport] = useState(null);   // { file, result }
  const [poiImporting, setPoiImporting] = useState(false);
  const [poiFilters, setPoiFilters] = useState({ zones: true, logos: true, labels: true });
  const [poiDrawMode, setPoiDrawMode] = useState(null);
  const [poiDraft, setPoiDraft] = useState(null);
  const [poiForm, setPoiForm] = useState(null);
  const [tripPath, setTripPath] = useState([]);
  const [tripInfo, setTripInfo] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeDest, setRouteDest] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [placingMe, setPlacingMe] = useState(false);
  const [nearby, setNearby] = useState([]);
  const [showRouteTool, setShowRouteTool] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyVehicle, setHistoryVehicle] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);  // LT1.5 — véhicule survolé dans la liste (repérage carte)
  const [meterForm, setMeterForm] = useState(null);   // P0.2 — relevé compteur {value}
  const [meterBusy, setMeterBusy] = useState(false);
  const [pendingFactor, setPendingFactor] = useState(null);  // P0.2 — {metric, factor} proposé
  const submitMeterReading = async () => {
    if (!selected || !meterForm || meterForm.value === "") return;
    const metric = selected.usage_metric === "engine_hours" ? "engine_hours" : "odometer";
    setMeterBusy(true);
    try {
      const r = await api.post(`/vehicles/${selected.id}/meter-reading`, { metric, real_value: Number(meterForm.value) });
      toast.success("Relevé enregistré");
      setMeterForm(null);
      if (r.data?.candidate_factor) setPendingFactor({ metric, factor: r.data.candidate_factor });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || "Relevé refusé (incohérent ?)"));
    } finally { setMeterBusy(false); }
  };
  const acceptFactor = async () => {
    if (!pendingFactor || !selected) return;
    try {
      await api.post(`/vehicles/${selected.id}/meter-calibration/accept`, { metric: pendingFactor.metric, factor: pendingFactor.factor, confirm: true });
      toast.success("Calibration appliquée aux valeurs futures");
      setPendingFactor(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || "Erreur"));
    }
  };
  const [maintList, setMaintList] = useState([]);      // P0.3 — échéances du véhicule sélectionné
  const [maintForm, setMaintForm] = useState(null);    // {label, due, notice, recur}
  const loadMaint = async (vid) => { try { const r = await api.get(`/maintenance?vehicle_id=${vid}`); setMaintList(r.data || []); } catch { setMaintList([]); } };
  const addMaint = async () => {
    if (!selected || !maintForm?.due) return;
    const isH = selected.usage_metric === "engine_hours";
    const body = { vehicle_id: selected.id, label: maintForm.label || "Entretien" };
    if (isH) { body.due_hours = Number(maintForm.due); body.notice_hours = Number(maintForm.notice || 0); if (maintForm.recur) body.recurrence_hours = Number(maintForm.recur); }
    else { body.due_km = Number(maintForm.due); body.notice_km = Number(maintForm.notice || 0); if (maintForm.recur) body.recurrence_km = Number(maintForm.recur); }
    try { await api.post("/maintenance", body); toast.success("Entretien ajouté"); setMaintForm(null); loadMaint(selected.id); } catch { toast.error("Erreur"); }
  };
  const doneMaint = async (mid) => { try { await api.post(`/maintenance/${mid}/done`, {}); toast.success("Marqué comme effectué"); loadMaint(selected.id); load(); } catch { toast.error("Erreur"); } };
  const delMaint = async (mid) => { if (!window.confirm("Supprimer cette échéance ?")) return; try { await api.delete(`/maintenance/${mid}`); loadMaint(selected.id); } catch { toast.error("Erreur"); } };
  useEffect(() => { if (selected && drawerTab === "maintenance") loadMaint(selected.id); /* eslint-disable-next-line */ }, [selected, drawerTab]);
  const [sensorTip, setSensorTip] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [ecoSummary, setEcoSummary] = useState(null);
  const [followId, setFollowId] = useState(null);
  const [navVehicleId, setNavVehicleId] = useState(null);  // LT1.4b — mode navigation (indépendant de followId/Suivre)
  const NAV_ENABLED = process.env.REACT_APP_NAVIGATION_ENABLED !== "false";  // LT1.4c — kill-switch mode navigation
  const [followFull, setFollowFull] = useState(false);
  const [ficheOpenMobile, setFicheOpenMobile] = useState(false);
  const [followTrace, setFollowTrace] = useState([]);
  const selectedId = useRef(null);
  const initialTabRef = useRef(null);   // P0.3.1 — onglet à ouvrir après sélection via ?vehicle=&tab=
  const urlHandledRef = useRef(false);
  const addrCache = useRef({});
  const sumCache = useRef({});

  // Résumés du jour : 1 requête bulk pour toute la flotte (au lieu de N appels), rafraîchie toutes les 60 s
  const resolveSummaries = useCallback(async () => {
    const now = Date.now();
    if (sumCache.current.at && now - sumCache.current.at < 60000) return;
    sumCache.current.at = now;
    try {
      const r = await api.get("/vehicles/daily-summaries");
      setSummaries(r.data);
    } catch {}
  }, []);

  const locLabel = useCallback((v) => {
    const lp = v?.last_position;
    if (lp && lp.lat != null) {
      const p = pois.find((x) => poiContains(lp.lat, lp.lon, x));
      if (p) return p.name;
    }
    return addresses[v?.id];
  }, [pois, addresses]);

  // Géocodage : uniquement les unités dont la position (arrondie) a changé, au plus GEOCODE_BATCH par cycle,
  // cache frontend par coordonnée arrondie (le serveur a aussi un cache borné).
  const GEOCODE_BATCH = 25;
  const resolveAddresses = useCallback(async (vlist) => {
    const todo = [];
    for (const v of vlist) {
      const lp = v.last_position;
      if (!lp || lp.lat == null) continue;
      const key = `${lp.lat.toFixed(3)},${lp.lon.toFixed(3)}`;
      if (addrCache.current[v.id] === key) continue;
      todo.push([v.id, key, lp]);
      if (todo.length >= GEOCODE_BATCH) break;
    }
    for (const [vid, key, lp] of todo) {
      addrCache.current[vid] = key;
      // Cache géocodage PARTAGÉ (lib/geocode) : même cache que AddressLabel (popups carte, SOS) →
      // aucune requête dupliquée, dédoublonnage des requêtes en vol.
      const addr = await reverseGeocode(lp.lat, lp.lon);
      setAddresses((prev) => ({ ...prev, [vid]: addr }));
    }
  }, []);

  const lastSync = useRef(null);
  const vehiclesRef = useRef([]);
  // Chargement complet (au montage, puis toutes les 60 s ou sur « Rafraîchir »)
  const load = useCallback(async () => {
    try {
      const [v, s, a] = await Promise.all([
        api.get("/vehicles"),
        api.get("/dashboard/stats"),
        api.get("/alerts?limit=8&acknowledged=false"),
      ]);
      vehiclesRef.current = v.data;
      // curseur serveur capturé AVANT la lecture de la flotte (jamais l'horloge du navigateur)
      lastSync.current = v.headers?.["x-sync-cursor"] || lastSync.current;
      setVehicles(v.data);
      setStats(s.data);
      setAlerts(a.data);
      if (selectedId.current) {
        const upd = v.data.find((x) => x.id === selectedId.current);
        if (upd) setSelected(upd);
        api.get(`/vehicles/${selectedId.current}/current-trip`).then((r) => setCurrentTrip(r.data)).catch(() => {});
      }
      resolveAddresses(v.data);
      resolveSummaries();
    } catch {} finally { setFirstLoad(false); }
  }, [resolveAddresses, resolveSummaries]);

  // Delta polling (10 s) : seules les unités modifiées depuis la dernière synchro sont renvoyées
  const loadDelta = useCallback(async () => {
    if (!lastSync.current) return load();
    try {
      const [r, a] = await Promise.all([
        api.get(`/vehicles/changes?since=${encodeURIComponent(lastSync.current)}`),
        api.get("/alerts?limit=8&acknowledged=false"),
      ]);
      setAlerts(a.data);
      lastSync.current = r.data.cursor || r.data.now;
      const changed = r.data.vehicles || [];
      if (changed.length) {
        const byId = Object.fromEntries(changed.map((x) => [x.id, x]));
        const merged = vehiclesRef.current.map((x) => byId[x.id] || x);
        vehiclesRef.current = merged;
        setVehicles(merged);
        if (selectedId.current && byId[selectedId.current]) {
          setSelected(byId[selectedId.current]);
          api.get(`/vehicles/${selectedId.current}/current-trip`).then((rr) => setCurrentTrip(rr.data)).catch(() => {});
        }
        resolveAddresses(changed);
      }
      resolveSummaries();
    } catch { load(); }
  }, [load, resolveAddresses, resolveSummaries]);

  useEffect(() => { setShare(null); setDrawerTab(initialTabRef.current || "resume"); initialTabRef.current = null; }, [selected?.id]);
  // P0.3.1 — deep-link depuis la vue globale Maintenance : ?vehicle=<id>&tab=maintenance
  useEffect(() => {
    if (urlHandledRef.current || firstLoad || !vehicles.length) return;
    const p = new URLSearchParams(window.location.search);
    const vid = p.get("vehicle");
    if (!vid) { urlHandledRef.current = true; return; }
    const v = vehicles.find((x) => x.id === vid);
    if (v) {
      urlHandledRef.current = true;
      initialTabRef.current = p.get("tab") || null;
      selectVehicle(v);
      window.history.replaceState({}, "", "/dashboard");
    }
  }, [firstLoad, vehicles]); // eslint-disable-line
  // LT1.2 — chargement PARESSEUX : trajets seulement à l'ouverture de l'onglet Trajets (aucun polling, une seule fois).
  useEffect(() => {
    if (!selected?.id) { setDayTrips(null); return; }
    setDayTrips(null);
  }, [selected?.id]);
  useEffect(() => {
    if (!selected?.id || drawerTab !== "trajets" || dayTrips !== null) return;
    api.get(`/vehicles/${selected.id}/trips`).then((r) => setDayTrips(r.data)).catch(() => setDayTrips({ count: 0, trips: [], total_km: 0 }));
  }, [selected?.id, drawerTab, dayTrips]);
  // LT1.2 — éco seulement à l'ouverture de l'onglet Éco (sentinelle _err pour éviter tout refetch en boucle).
  useEffect(() => {
    if (!selected?.id) { setEcoSummary(null); return; }
    setEcoSummary(null);
  }, [selected?.id]);
  useEffect(() => {
    if (!selected?.id || drawerTab !== "eco" || ecoSummary !== null) return;
    api.get(`/vehicles/${selected.id}/eco-summary`).then((r) => setEcoSummary(r.data)).catch(() => setEcoSummary({ enabled: false, counts: {}, _err: true }));
  }, [selected?.id, drawerTab, ecoSummary]);
  useEffect(() => {
    const handler = async (e) => {
      const vid = e.detail?.vehicle_id;
      if (!vid) return;
      let v = vehicles.find((x) => x.id === vid);
      if (!v) { try { v = (await api.get(`/vehicles/${vid}`)).data; } catch { return; } }
      setSelected(v); selectedId.current = v.id;
      setPlayback(null); setRoutePath([]); setRouteInfo(null); setRouteDest(null);
      setShowTrips(true);
      try {
        const r = await api.get(`/vehicles/${v.id}/trips`);
        const trips = r.data?.trips || [];
        const allPts = trips.flatMap((t) => t.points || []);
        if (allPts.length > 1) {
          setTripPath(allPts);
          setTripInfo({ start: trips[0].start, end: trips[trips.length - 1].end, distance_km: r.data.total_km,
            duration_min: trips.reduce((a, t) => a + (t.duration_min || 0), 0), points: allPts });
        }
      } catch {}
    };
    window.addEventListener("assistant:show-trips", handler);
    return () => window.removeEventListener("assistant:show-trips", handler);
  }, [vehicles]);
  useEffect(() => {
    setTripAddr({});
    if (!dayTrips?.trips?.length) return;
    let cancel = false;
    (async () => {
      for (let i = 0; i < dayTrips.trips.length; i++) {
        const t = dayTrips.trips[i];
        if (!t.points?.length) continue;
        const s = t.points[0], e = t.points[t.points.length - 1];
        try {
          const rs = await api.get(`/geocode/reverse?lat=${s.lat}&lon=${s.lon}`, { timeout: 8000 });
          const re = await api.get(`/geocode/reverse?lat=${e.lat}&lon=${e.lon}`, { timeout: 8000 });
          if (!cancel) setTripAddr((a) => ({ ...a, [i]: { start: rs.data.address || "", end: re.data.address || "" } }));
        } catch {
          // Échec / expiration : état explicite (pas de « localisation… » permanent).
          if (!cancel) setTripAddr((a) => ({ ...a, [i]: { start: "", end: "" } }));
        }
      }
    })();
    return () => { cancel = true; };
  }, [dayTrips]);

  const toggleEco = async () => {
    if (!selected) return;
    const next = !ecoSummary?.enabled;
    try {
      const r = await api.put(`/vehicles/${selected.id}/eco-config`, { eco_driving_enabled: next });
      setEcoSummary((e) => ({ ...(e || { counts: {} }), enabled: next }));
      const ec = r.data?.eco_check;
      if (next && ec) {
        toast.success(ec.transmitted
          ? `Éco-conduite activée — vérification « ${ec.command_text} » envoyée au boîtier ✓ (réponse dans Commandes)`
          : `Éco-conduite activée — boîtier hors ligne, vérification « ${ec.command_text} » en file`);
      } else {
        toast.success(next ? "Éco-conduite activée" : "Éco-conduite désactivée");
      }
      load();
    } catch { toast.error("Impossible de modifier l'éco-conduite"); }
  };

  const toggleCanField = async (key) => {
    if (!selected?.last_position?.can) return;
    const avail = canEntries(selected.last_position.can).map((f) => f.key);
    const cur = (selected.can_display && selected.can_display.length) ? selected.can_display : avail;
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    setSelected((s) => (s ? { ...s, can_display: next } : s));
    try { await api.put(`/vehicles/${selected.id}/can-display`, { fields: next }); }
    catch { toast.error("Impossible d'enregistrer l'affichage CAN"); }
  };

  const createShare = async () => {
    try {
      const r = await api.post(`/vehicles/${selected.id}/share`, { hours: 24 });
      const url = `${window.location.origin}/share/${r.data.token}`;
      setShare(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Lien de partage copié (valide 24 h)");
    } catch {
      toast.error("Impossible de créer le lien de partage");
    }
  };

  useEffect(() => {
    api.get("/geofences").then((r) => setGeofences(r.data)).catch(() => {});
    loadPois();
    load();
    const t = setInterval(loadDelta, 10000);
    const full = setInterval(load, 60000);
    return () => { clearInterval(t); clearInterval(full); };
  }, [load, loadDelta]);

  const loadPois = () => api.get("/pois").then((r) => setPois(r.data)).catch(() => {});

  const downloadPoiTemplate = () => {
    const csv = "nom,latitude,longitude,categorie,rayon,couleur,note\n"
      + "Depot Ducos,-22.2758,166.4180,depot,150,#F5821F,Entrepot principal\n"
      + "Station Total Magenta,-22.2680,166.4600,station,,,\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = "modele_import_poi.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const submitPoiImport = async (e) => {
    e?.preventDefault?.();
    if (!poiImport?.file) { toast.error("Choisissez un fichier CSV ou Excel"); return; }
    setPoiImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", poiImport.file);
      const r = await api.post("/pois/import", fd);
      setPoiImport((s) => ({ ...s, result: r.data, file: null }));
      toast.success(`${r.data.created} POI importé(s)${r.data.skipped ? ` · ${r.data.skipped} ignoré(s)` : ""}`);
      loadPois();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    }
    setPoiImporting(false);
  };

  const refreshNow = async () => {
    setRefreshing(true);
    try { await load(); loadPois(); loadFilterRefs().catch(() => {}); const r = await api.get("/geofences"); setGeofences(r.data); toast.success("Données actualisées"); }
    catch { /* ignore */ } finally { setTimeout(() => setRefreshing(false), 600); }
  };

  const startPoiPoint = () => { setPoiDrawMode("point"); setPoiDraft({ mode: "point", category: "client", color: "#17A2B8" }); };
  const startPoiPolygon = () => { setPoiDrawMode("polygon"); setPoiDraft({ mode: "polygon", coordinates: [], category: "client", color: "#17A2B8" }); };
  const startPoiTroncon = () => { setPoiDrawMode("troncon"); setPoiDraft({ mode: "troncon", points: [], coordinates: [], category: "autre", color: "#F5821F" }); };
  const cancelPoiDraw = () => { setPoiDrawMode(null); setPoiDraft(null); };

  const onPoiMapClick = async (latlng) => {
    if (poiDrawMode === "point") {
      setPoiDrawMode(null);
      setPoiForm({ type: "point", name: "", category: "client", color: "#17A2B8", note: "", radius: null, lat: latlng.lat, lon: latlng.lon, company_id: defaultPoiCompany() });
      setPoiDraft({ mode: "point", ...latlng, category: "client", color: "#17A2B8" });
    } else if (poiDrawMode === "polygon") {
      setPoiDraft((d) => ({ ...d, coordinates: [...(d?.coordinates || []), latlng] }));
    } else if (poiDrawMode === "troncon") {
      const prev = poiDraft?.points?.[poiDraft.points.length - 1];
      if (!prev) { setPoiDraft((d) => ({ ...d, points: [latlng], coordinates: [latlng] })); return; }
      try {
        const r = await api.get(`/route?from_lat=${prev.lat}&from_lon=${prev.lon}&to_lat=${latlng.lat}&to_lon=${latlng.lon}`);
        const seg = (r.data.geometry || []).map(([la, lo]) => ({ lat: la, lon: lo }));
        setPoiDraft((d) => ({ ...d, points: [...(d.points || []), latlng], coordinates: [...(d.coordinates || []), ...seg.slice(1)] }));
      } catch {
        setPoiDraft((d) => ({ ...d, points: [...(d.points || []), latlng], coordinates: [...(d.coordinates || []), latlng] }));
        toast.error("Route indisponible — segment en ligne droite");
      }
    }
  };

  const finishPoiPolygon = () => {
    if ((poiDraft?.coordinates?.length || 0) < 3) return toast.error("Ajoutez au moins 3 sommets");
    setPoiDrawMode(null);
    setPoiForm({ type: "polygon", name: "", category: "client", color: "#17A2B8", note: "", coordinates: poiDraft.coordinates, company_id: defaultPoiCompany() });
  };

  const finishPoiTroncon = () => {
    if ((poiDraft?.coordinates?.length || 0) < 2) return toast.error("Cliquez au moins 2 points le long de la route");
    setPoiDrawMode(null);
    setPoiForm({ type: "troncon", name: "", category: "autre", color: poiDraft.color || "#F5821F", note: "", coordinates: poiDraft.coordinates, width_m: 30, company_id: defaultPoiCompany() });
  };

  const savePoi = async () => {
    if (!poiForm.name.trim()) return toast.error("Nommez le POI");
    if (!isAdmin && !poiForm.company_id) return toast.error("Choisissez une compagnie pour enregistrer ce POI.");
    const body = { ...poiForm, note: poiForm.note || "", company_id: poiForm.company_id || null };
    try {
      if (poiForm.id) await api.put(`/pois/${poiForm.id}`, body);
      else await api.post("/pois", body);
      toast.success(poiForm.id ? "POI modifié" : "POI créé");
      setPoiForm(null); setPoiDraft(null);
      loadPois();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deletePoi = async () => {
    if (!poiForm?.id) return;
    try { await api.delete(`/pois/${poiForm.id}`); toast.success("POI supprimé"); setPoiForm(null); setPoiDraft(null); loadPois(); }
    catch { toast.error("Erreur"); }
  };

  const openPoiEdit = (p) => setPoiForm({ ...p, radius: p.radius ?? null, width_m: p.width_m ?? 30, note: p.note || "", company_id: p.company_id || "" });

  // Playback advance loop
  useEffect(() => {
    if (!playback?.playing) return;
    const iv = setInterval(() => {
      setPlayback((pb) => {
        if (!pb) return pb;
        if (pb.idx >= pb.track.length - 1) return { ...pb, playing: false };
        return { ...pb, idx: pb.idx + 1 };
      });
    }, 700 / playback.speed);
    return () => clearInterval(iv);
  }, [playback?.playing, playback?.speed]);

  const selectVehicle = async (v) => {
    setSelected(v);
    selectedId.current = v.id;
    setMobilePanel(null);
    setFicheOpenMobile(false);
    setCurrentTrip(null);
    setTripPath([]);
    setTripInfo(null);
    api.get(`/vehicles/${v.id}/current-trip`).then((r) => setCurrentTrip(r.data)).catch(() => {});
    // Afficher automatiquement le dernier trajet (ou celui en cours) coloré sur la carte
    api.get(`/vehicles/${v.id}/trips`).then((r) => {
      const trips = r.data?.trips || [];
      const last = trips[trips.length - 1];
      if (selectedId.current === v.id && last && (last.points || []).length > 1) {
        setTripPath(last.points);
        setTripInfo({ start: last.start, end: last.end, distance_km: last.distance_km, duration_min: last.duration_min, points: last.points });
      }
    }).catch(() => {});
    try {
      const r = await api.get(`/vehicles/${v.id}/track?hours=2`);
      setTrack(r.data);
    } catch { setTrack([]); }
  };

  const sendCut = async (payload = {}) => {
    const action = cutDialog; setCutDialog(null);
    if (!selected) return;
    try {
      const r = await api.post(`/vehicles/${selected.id}/command`, { type: action === "cut" ? "cut_engine" : "restore_engine", ...payload });
      setSelected((s) => (s ? { ...s, engine_cut: r.data.engine_cut } : s));
      toast.success(r.data.transmitted ? "Commande transmise au boîtier ✓" : "Boîtier hors ligne — commande en file, transmise à la reconnexion");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur (immobilisation non autorisée ou conditions non réunies)"); }
  };

  const clearSelection = () => { setSelected(null); selectedId.current = null; setTrack([]); setPlayback(null); setCurrentTrip(null); setTripPath([]); setTripInfo(null); setRoutePath([]); setRouteInfo(null); setRouteDest(null); };

  const ackSos = async (vid) => {
    try { await api.post(`/vehicles/${vid}/sos-ack`); toast.success("SOS acquitté"); load(); }
    catch { toast.error("Échec de l'acquittement"); }
  };
  const sosBannerTop = vehicles.some((v) => v.sos_active) ? "top-28" : "top-16";
  const fmtSensorDT = (ts) => { try { return new Date(ts).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  const showSensorTip = (e, label, value, ts) => { const r = e.currentTarget.getBoundingClientRect(); setSensorTip({ label, value, ts, x: Math.min(r.left, window.innerWidth - 236), y: r.bottom }); };

  const mapQuickAction = async (action, v) => {
    selectVehicle(v);
    if (action === "follow") { setFollowId(v.id); return; }
    if (action === "trips") { setShowTrips(true); return; }
    if (action === "commands") { setShowControl(true); return; }
    if (action === "replay") {
      try {
        const r = await api.get(`/vehicles/${v.id}/track?hours=1`);
        const pts = r.data;
        if (pts.length >= 2) setPlayback({ track: pts, idx: 0, playing: true, speed: 2, hours: 1 });
        else toast.info("Pas assez de points sur la dernière heure pour rejouer");
      } catch { toast.error("Erreur de lecture du trajet"); }
    }
  };

  const selectTrip = (t) => { setTripInfo(t); setTripPath(t.points || []); setShowTrips(false); setPlayback(null); };

  const exportRaw = async (v, fmt) => {
    try {
      const r = await api.get(`/vehicles/${v.id}/raw?hours=24`);
      const rows = r.data;
      if (!rows.length) return toast.error("Aucune donnée sur 24h");
      let blob;
      if (fmt === "json") blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
      else {
        const cols = ["timestamp", "lat", "lon", "speed", "angle", "altitude", "ignition", "satellites", "gsm_signal", "external_voltage", "battery_voltage"];
        const csv = [cols.join(",")].concat(rows.map((p) => cols.map((c) => p[c] ?? "").join(","))).join("\n");
        blob = new Blob([csv], { type: "text/csv" });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(v.plate || v.name || v.id).replace(/\s/g, "")}_raw_24h.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} (${rows.length} points)`);
    } catch { toast.error("Échec de l'export"); }
  };
  const openHistory = (v) => { setHistoryVehicle(v); setShowHistory(true); };
  const applyRoute = (coords, info, dest) => { setRoutePath(coords.map(([lat, lon]) => ({ lat, lon }))); setRouteInfo(info); setRouteDest(dest); setPlayback(null); setTripPath([]); setTripInfo(null); };
  const clearRoute = () => { setRoutePath([]); setRouteInfo(null); setRouteDest(null); };

  const fetchNearest = async (pos) => {
    try { const r = await api.get(`/nearest-vehicles?lat=${pos.lat}&lon=${pos.lon}&limit=6`); setNearby(r.data); }
    catch { setNearby([]); }
  };
  const useMyGps = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation indisponible — cliquez sur la carte"); setPlacingMe(true); return; }
    toast.info("Localisation en cours…");
    navigator.geolocation.getCurrentPosition(
      (p) => { const pos = { lat: p.coords.latitude, lon: p.coords.longitude }; setMyPos(pos); setPlacingMe(false); fetchNearest(pos); },
      () => { toast.error("GPS refusé — cliquez sur la carte pour placer votre position"); setPlacingMe(true); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  const onPickMe = (latlng) => { setMyPos(latlng); setPlacingMe(false); fetchNearest(latlng); };
  const routeToVehicle = async (v) => {
    if (!myPos) return;
    try {
      const r = await api.get(`/route?from_lat=${myPos.lat}&from_lon=${myPos.lon}&to_lat=${v.lat}&to_lon=${v.lon}`);
      applyRoute(r.data.geometry, { distance_km: r.data.distance_km, duration_min: r.data.duration_min }, { lat: v.lat, lon: v.lon, label: v.name });
      setFollowId(v.id);
      toast.success(`Itinéraire vers ${v.name} · ${r.data.distance_km} km — suivi activé`);
    } catch { toast.error("Itinéraire indisponible"); }
  };
  const routeToNearest = () => { if (nearby[0]) routeToVehicle(nearby[0]); };
  const createIntervention = async (v) => {
    try {
      const r = await api.post("/interventions", { vehicle_id: v.id });
      const url = `${window.location.origin}/intervention/${r.data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Lien d'intervention copié — envoyez-le à l'intervenant"); }
      catch { toast.success(`Lien: ${url}`); }
    } catch { toast.error("Création du lien impossible"); }
  };
  const clearMyPos = () => { setMyPos(null); setNearby([]); setPlacingMe(false); clearRoute(); };

  const replayTrip = () => {
    const pts = tripInfo?.points || [];
    if (pts.length < 2) return;
    setTripPath([]);
    setPlayback({ track: pts, idx: 0, playing: true, speed: 2 });
  };

  const exportGpx = () => {
    const pts = tripInfo?.points || [];
    if (!pts.length) return;
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="Boomerang Pacific Tracking" xmlns="http://www.topografix.com/GPX/1/1">\n<trk><name>${selected?.name || "Trajet"}</name><trkseg>\n` +
      pts.map((p) => `<trkpt lat="${p.lat}" lon="${p.lon}"><time>${p.timestamp || ""}</time><speed>${(p.speed / 3.6).toFixed(2)}</speed></trkpt>`).join("\n") +
      `\n</trkseg></trk>\n</gpx>`;
    const url = URL.createObjectURL(new Blob([gpx], { type: "application/gpx+xml" }));
    const a = document.createElement("a"); a.href = url; a.download = `trajet_${selected?.name || "boomerang"}.gpx`; a.click(); URL.revokeObjectURL(url);
    toast.success("GPX exporté");
  };

  const exportPdf = async () => {
    const pts = tripInfo?.points || [];
    if (!pts.length) return;
    toast.info("Génération du PDF…");
    const rev = async (p) => { try { const r = await api.get(`/geocode/reverse?lat=${p.lat}&lon=${p.lon}`); return r.data.address; } catch { return ""; } };
    const [sa, ea] = [await rev(pts[0]), await rev(pts[pts.length - 1])];
    const body = {
      vehicle_name: selected?.name || "Véhicule",
      date: tripInfo.start ? new Date(tripInfo.start).toLocaleDateString("fr-FR") : "",
      start: tripInfo.start ? new Date(tripInfo.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "",
      end: tripInfo.end ? new Date(tripInfo.end).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "",
      distance_km: tripInfo.distance_km || 0, duration_min: tripInfo.duration_min || 0,
      start_address: sa, end_address: ea, points: pts,
    };
    try {
      const r = await api.post("/trips/export-pdf", body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "trajet_boomerang.pdf"; a.click(); URL.revokeObjectURL(url);
      toast.success("PDF exporté");
    } catch { toast.error("Erreur export PDF"); }
  };

  const startPlayback = async (hours) => {
    if (!selected) return;
    setTripPath([]);
    const r = await api.get(`/vehicles/${selected.id}/track?hours=${hours}`);
    const pts = r.data;
    if (pts.length < 2) return;
    setPlayback({ track: pts, idx: 0, playing: true, speed: 2, hours });
  };

  // Références de filtres (sociétés + groupes) : chargées 1× au montage et sur Rafraîchir.
  // JAMAIS dans le polling delta 10 s, JAMAIS une requête par société.
  const loadFilterRefs = useCallback(async () => {
    const [c, p] = await Promise.all([api.get("/companies"), api.get("/pools")]);
    setCompanies(c.data || []);
    setPools(p.data || []);
  }, []);

  useEffect(() => {
    loadFilterRefs().catch(() => {});
  }, [loadFilterRefs]);

  // P0.2.5 §9 — si le groupe sélectionné n'appartient pas aux sociétés filtrées, le réinitialiser (UX : reset).
  useEffect(() => {
    if (poolFilter && companyFilters.length > 0) {
      const p = pools.find((x) => x.id === poolFilter);
      if (p && !companyFilters.includes(p.company_id)) setPoolFilter("");
    }
  }, [companyFilters, poolFilter, pools]);

  const poolVids = poolFilter ? new Set((pools.find((p) => p.id === poolFilter)?.vehicle_ids) || []) : null;
  const filtered = vehicles.filter((v) =>
    !v.hidden_on_live &&
    (followId ? v.id === followId : true) &&
    (v.name + v.plate + (v.driver || "")).toLowerCase().includes(q.toLowerCase()) &&
    (companyFilters.length === 0 || companyFilters.includes(v.company_id)) &&
    (!poolVids || poolVids.has(v.id)) &&
    (vehFilter.length === 0 || vehFilter.includes(v.id))
  );

  const followVehicle = followId ? vehicles.find((v) => v.id === followId) : null;
  const navVehicle = navVehicleId ? (vehicles.find((v) => v.id === navVehicleId) || null) : null;
  const followPos = followVehicle?.last_position?.lat != null
    ? [followVehicle.last_position.lat, followVehicle.last_position.lon] : null;
  const shownAlerts = followId
    ? alerts.filter((a) => a.vehicle_id === followId || a.vehicle === followVehicle?.name)
    : alerts;

  const cur = playback ? playback.track[playback.idx] : null;
  const visiblePois = pois.filter((p) => companyFilters.length === 0 || companyFilters.includes(p.company_id));
  const lastTp = tripPath.length ? tripPath[tripPath.length - 1] : null;
  const tripLive = !!(lastTp && selected && ["moving", "idling"].includes(selected.status) &&
    lastTp.timestamp && (Date.now() - new Date(lastTp.timestamp).getTime() < 15 * 60 * 1000));

  return (
    <Layout fullBleed>
      <div className="relative h-[calc(100vh-4rem)] w-full overflow-hidden">
        <FleetMap
          vehicles={filtered}
          geofences={geofences}
          track={selected && !playback ? track : []}
          selected={selected}
          onSelect={selectVehicle}
          onQuickAction={mapQuickAction}
          playback={playback}
          pois={visiblePois}
          poiFilters={poiFilters}
          poiDrawMode={poiDrawMode}
          poiDraft={poiDraft}
          onPoiMapClick={onPoiMapClick}
          onPoiClick={openPoiEdit}
          tripPath={tripPath}
          tripLive={tripLive}
          routePath={routePath}
          routeDest={routeDest}
          fitAll={!followId}
          follow={followPos}
          followTrace={followId ? followTrace : []}
          draftWidth={poiForm?.type === "troncon" ? (poiForm.width_m ?? 30) : null}
          userPos={myPos}
          placingMe={placingMe}
          onPickMe={onPickMe}
          hoveredVehicleId={hoveredId}
          followId={followId}
          navVehicleId={navVehicleId}
          hoverPanDisabled={!!followId || !!navVehicleId}
        />

        <div className="absolute top-3 left-3 lg:left-[404px] z-[540]" data-testid="mepos-control">
          {!myPos ? (
            <div className="glass rounded-lg p-1.5 flex items-center gap-1.5">
              <button onClick={useMyGps} data-testid="mepos-gps" className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-muted-foreground hover:text-[#2563EB]"><LocateFixed className="w-3.5 h-3.5" /> Ma position</button>
              <button onClick={() => setPlacingMe((v) => !v)} data-testid="mepos-pick" className={`hidden lg:inline-flex px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${placingMe ? "border-[#2563EB] text-[#2563EB] bg-[#2563EB]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>Cliquer sur la carte</button>
            </div>
          ) : (
            <div className="glass rounded-lg p-2 w-64 animate-slidein" data-testid="mepos-panel">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold flex items-center gap-1 text-[#2563EB]"><LocateFixed className="w-3.5 h-3.5" /> Ma position</span>
                <button onClick={clearMyPos} data-testid="mepos-clear" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              <button onClick={routeToNearest} data-testid="mepos-nearest" disabled={!nearby.length} className="w-full mb-2 flex items-center justify-center gap-1 bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-40 text-white text-xs font-bold py-1.5 rounded-md"><RouteArrow className="w-3.5 h-3.5" /> Rejoindre le plus proche</button>
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {nearby.map((v) => (
                  <div key={v.id} className="flex items-center justify-between gap-2 text-xs bg-secondary/40 rounded px-2 py-1" data-testid={`mepos-veh-${v.id}`}>
                    <span className="min-w-0"><span className="font-semibold truncate block">{v.name}</span><span className="text-muted-foreground data">{v.distance_km} km</span></span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => routeToVehicle(v)} data-testid={`mepos-route-${v.id}`} className="flex items-center gap-1 text-[#2563EB] hover:underline font-semibold"><RouteArrow className="w-3.5 h-3.5" /> Itinéraire</button>
                      <button onClick={() => createIntervention(v)} data-testid={`mepos-share-${v.id}`} title="Générer un lien d'intervention (partage de position en direct)" className="text-muted-foreground hover:text-[#2563EB]"><Link2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
                {!nearby.length && <p className="text-[11px] text-muted-foreground text-center py-2">Aucun véhicule localisé</p>}
              </div>
            </div>
          )}
        </div>

        {followId && followVehicle && (
          <div className="absolute top-14 left-1/2 -translate-x-1/2 z-[560] glass rounded-full px-4 py-2 flex items-center gap-3 animate-slidein border border-[#F5821F]/50" data-testid="follow-banner">
            <span className="flex items-center gap-2 text-xs font-semibold text-[#F5821F]"><Crosshair className="w-4 h-4" /> Mode suivi : {followVehicle.name}</span>
            <button onClick={() => setFollowFull((f) => !f)} data-testid="follow-fullscreen-btn" className="text-[11px] font-bold text-foreground hover:text-[#2ec5db] flex items-center gap-1">{followFull ? <><Minimize2 className="w-3.5 h-3.5" /> Réduire</> : <><Maximize2 className="w-3.5 h-3.5" /> Plein écran</>}</button>
            <button onClick={() => { setFollowId(null); setFollowFull(false); }} data-testid="follow-exit-btn" className="text-[11px] font-bold text-foreground hover:text-[#F5821F] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Quitter</button>
          </div>
        )}

        {!followFull && <PoiPanel
          poiDrawMode={poiDrawMode}
          poiDraft={poiDraft}
          poiFilters={poiFilters}
          setPoiFilters={setPoiFilters}
          onStartPoint={startPoiPoint}
          onStartPolygon={startPoiPolygon}
          onStartTroncon={startPoiTroncon}
          onCancelDraw={cancelPoiDraw}
          onFinishPolygon={finishPoiPolygon}
          onFinishTroncon={finishPoiTroncon}
          poiForm={poiForm}
          setPoiForm={setPoiForm}
          onSave={savePoi}
          onDelete={deletePoi}
          companies={companies}
          companyFilter={companyFilters.length === 1 ? companyFilters[0] : ""}
          requireCompany={!isAdmin}
          canManage={canManagePoi}
          showImport={canManagePoi && !isAdmin}
          onImportClick={() => setPoiImport({ file: null, result: null })}
        />}
        {poiImport && (
          <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="poi-import-modal" onClick={() => setPoiImport(null)}>
            <form onSubmit={submitPoiImport} onClick={(e) => e.stopPropagation()}
              className="bg-card border border-border rounded-xl w-full max-w-md p-5 space-y-3 shadow-2xl">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-base">Importer des POI (CSV / Excel)</h3>
                <button type="button" onClick={() => setPoiImport(null)} data-testid="poi-import-close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-muted-foreground">
                Colonnes : <b>nom</b>, <b>latitude</b>, <b>longitude</b> (ou <i>coordonnees</i> « lat,lon »),
                <b> categorie</b> (client/depot/station/fournisseur/autre), <b>rayon</b>, <b>couleur</b>, <b>note</b>.
                Les POI seront créés dans <b>votre société</b>.
                <button type="button" onClick={downloadPoiTemplate} className="ml-1 text-[#17A2B8] font-semibold hover:underline">Télécharger le modèle</button>
              </p>
              <input type="file" accept=".csv,.xlsx" data-testid="poi-import-file"
                onChange={(e) => setPoiImport((s) => ({ ...s, file: e.target.files?.[0] || null, result: null }))}
                className="w-full text-sm border border-border rounded-md px-3 py-2 bg-background" />
              {poiImport.result && (
                <div className="rounded-md border border-border bg-secondary/30 p-3 text-xs space-y-1" data-testid="poi-import-result">
                  <p className="font-semibold text-[#10B981]">{poiImport.result.created} POI importé(s) sur {poiImport.result.total} ligne(s).</p>
                  {poiImport.result.skipped > 0 && (
                    <ul className="list-disc pl-5 max-h-40 overflow-y-auto text-muted-foreground">
                      {poiImport.result.errors.map((er, i) => <li key={i}>Ligne {er.line} : {er.reason}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {poiImporting
                ? <div className="flex justify-center py-2"><BrandLoader size={36} rings={false} label="Import en cours…" /></div>
                : <button type="submit" data-testid="poi-import-submit" className="w-full bg-[#F5821F] text-white font-semibold py-2.5 rounded-md hover:opacity-90">Importer</button>}
            </form>
          </div>
        )}

        {!panelOpen && !followFull && (
          <button onClick={() => setPanelOpen(true)} data-testid="panel-expand-btn" title="Afficher la liste des véhicules" className="hidden lg:flex absolute top-3 left-3 z-[551] glass rounded-md px-2.5 py-2 items-center gap-1 text-sm font-semibold hover:text-[#17A2B8] transition-colors"><ChevronRight className="w-4 h-4" /> Véhicules</button>
        )}
        {mobilePanel !== "list" && !followFull && (
          <button onClick={() => setMobilePanel("list")} data-testid="mobile-list-open" title="Afficher les véhicules"
            className="lg:hidden absolute top-1/2 -translate-y-1/2 left-0 z-[551] glass rounded-r-xl py-5 px-1.5 flex items-center animate-slidein">
            <ChevronRight className="w-5 h-5 brand-teal" />
          </button>
        )}
        {/* Overlay mobile — ferme le tiroir véhicules au tap */}
        {mobilePanel === "list" && !followFull && (
          <div className="lg:hidden absolute inset-0 bottom-16 bg-black/40 z-[549] animate-fadein" onClick={() => setMobilePanel(null)} data-testid="mobile-panel-overlay" />
        )}
        {/* Vehicle panel — LT1.1 (FleetList) */}
        <FleetList
          filtered={filtered}
          count={filtered.length}
          firstLoad={firstLoad}
          vehiclesRaw={vehicles}
          selected={selected}
          compact={compact} setCompact={setCompact}
          q={q} setQ={setQ}
          companies={companies} isAdmin={isAdmin}
          companyFilters={companyFilters} setCompanyFilters={setCompanyFilters}
          pools={pools} poolFilter={poolFilter} setPoolFilter={setPoolFilter}
          vehFilter={vehFilter} setVehFilter={setVehFilter}
          vehOpen={vehOpen} setVehOpen={setVehOpen}
          panelOpen={panelOpen} mobilePanel={mobilePanel} followFull={followFull}
          setPanelOpen={setPanelOpen} setMobilePanel={setMobilePanel}
          refreshing={refreshing} refreshNow={refreshNow}
          setShowDevices={setShowDevices} setShowRouteTool={setShowRouteTool}
          selectVehicle={selectVehicle} locLabel={locLabel} summaries={summaries}
          setHoverInfo={setHoverInfo} showSensorTip={showSensorTip} setSensorTip={setSensorTip}
          setSensorChart={setSensorChart} setFuelChart={setFuelChart}
          openHistory={openHistory} exportRaw={exportRaw}
          onVehicleHover={(v) => setHoveredId(v ? v.id : null)}
        />

        {NAV_ENABLED && navVehicleId && (
          <NavigationOverlay
            vehicle={navVehicle || (selected?.id === navVehicleId ? selected : null)}
            vehicles={vehicles}
            onExit={() => setNavVehicleId(null)}
          />
        )}

        {/* Selected detail */}
        {selected && !playback && !followId && (
          <VehicleDrawer
            vehicle={selected}
            cls={`absolute bottom-20 lg:bottom-5 left-3 right-3 lg:left-[352px] lg:right-auto w-auto lg:w-[360px] max-h-[72vh] lg:max-h-[calc(100vh-2.5rem)] overflow-y-auto glass rounded-lg p-4 z-[560] animate-slidein ${tripPath.length > 1 && !ficheOpenMobile ? "hidden lg:block" : ""}`}
            following={followId === selected.id}
            engineCut={selected.engine_cut}
            onFollow={() => { if (followId === selected.id) { setFollowId(null); setFollowFull(false); } else setFollowId(selected.id); }}
            onJoin={NAV_ENABLED ? () => setNavVehicleId(selected.id) : undefined}
            navEnabled={NAV_ENABLED}
            onTrips={() => setShowTrips(true)}
            onHistory={() => openHistory(selected)}
            onCommands={() => setShowControl(true)}
            onCut={canImmobilize ? () => setCutDialog(selected.engine_cut ? "restore" : "cut") : undefined}
            onClose={clearSelection}
            activeTab={drawerTab}
            onTabChange={setDrawerTab}
            lastSeen={selected.last_position?.timestamp ? timeAgo(selected.last_position.timestamp) : null}
            company={selected.company_name || companies.find((c) => c.id === selected.company_id)?.name || null}
          >
            {/* ===== ONGLET RÉSUMÉ ===== */}
            {drawerTab === "resume" && (<div data-testid="tab-resume">
            {(() => { const ss = statusSince(selected); return (
              <div className="mb-3 flex items-center gap-2 rounded-md border border-border px-3 py-2" data-testid="status-since">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${!ss.fresh ? "bg-zinc-500" : ss.moving ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="font-bold text-sm" data-testid="status-since-text">{!ss.fresh ? "Dernier état connu · " : ""}{ss.label} {ss.dur}</span>
                {!ss.fresh && ss.gpsTs && <span className="text-[10px] text-muted-foreground ml-auto" data-testid="status-stale">position {timeAgo(new Date(ss.gpsTs).toISOString())}</span>}
              </div>
            ); })()}
            <div className="grid grid-cols-2 gap-3 mt-1 data text-xs">
              <Detail label="Vitesse" value={businessStatus(selected).fresh ? fmtSpeed(selected.last_position?.speed, isMarine(selected)) : "—"} icon={Gauge} />
              <Detail label="Cap" value={`${selected.last_position?.angle ?? 0}°`} icon={Navigation} />
              <Detail label="Contact" value={selected.last_position?.ignition ? "Mis" : "Coupé"} />
              <Detail label={selected.usage_metric === "engine_hours" ? "Temps moteur" : "Odomètre"} value={usageText(selected)} icon={Route} />
              {(selected.current_driver_name || selected.driver || "").trim() && <Detail label="Conducteur" value={(selected.current_driver_name || selected.driver).trim()} icon={UserRound} />}
              {companies.find((c) => c.id === selected.company_id) && (
                <Detail label="Société" value={companies.find((c) => c.id === selected.company_id)?.name} />
              )}
            </div>
            <div className="mt-3">
              <p className="label-mono mb-1">Emplacement en temps réel</p>
              <p className="text-xs font-semibold flex items-start gap-1.5" data-testid="detail-address">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 brand-orange" />
                <span>{locLabel(selected) === undefined ? "Localisation en cours…" : (locLabel(selected) || "Adresse indisponible")}</span>
              </p>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 data text-xs">
              <Detail label="Dernière communication" value={selected.last_gprs_at ? timeAgo(selected.last_gprs_at) : (selected.last_position?.timestamp ? timeAgo(selected.last_position.timestamp) : "—")} icon={History} testid="detail-gprs" />
              <Detail label="Dernière position GPS" value={(selected.last_gps_at || selected.last_position?.timestamp) ? timeAgo(selected.last_gps_at || selected.last_position.timestamp) : "—"} testid="detail-gps" />
            </div>
            {selected.last_position && (
              <div className="mt-3" data-testid="detail-sensors">
                <p className="label-mono mb-1.5">Capteurs — cliquer pour l'historique</p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.last_position.external_voltage != null && (
                    <SensorBubble icon={Zap} value={`${(selected.last_position.external_voltage / 1000).toFixed(1)}v`} color={METRIC_COLORS.voltage}
                      testid="detail-sensor-voltage" title="Alimentation" onClick={() => setSensorChart({ vehicle: selected, metric: "voltage" })} />
                  )}
                  {battPct(selected.last_position.battery_voltage) != null && (
                    <SensorBubble icon={BatteryMedium} value={`${battPct(selected.last_position.battery_voltage)}%`} color={METRIC_COLORS.battery}
                      testid="detail-sensor-battery" title="Batterie" onClick={() => setSensorChart({ vehicle: selected, metric: "battery" })} />
                  )}
                  {selected.last_position.satellites != null && (
                    <SensorBubble icon={Satellite} value={selected.last_position.satellites} color={METRIC_COLORS.satellites}
                      testid="detail-sensor-sats" title="Satellites" onClick={() => setSensorChart({ vehicle: selected, metric: "satellites" })} />
                  )}
                  {selected.last_position.gsm_signal != null && (
                    <SensorBubble icon={SignalHigh} value={`${selected.last_position.gsm_signal}/5`} color={METRIC_COLORS.gsm}
                      testid="detail-sensor-gsm" title="Signal GSM" onClick={() => setSensorChart({ vehicle: selected, metric: "gsm" })} />
                  )}
                </div>
              </div>
            )}
            <div className="mt-4">
              <p className="label-mono mb-2">Partage de position</p>
              <button onClick={createShare} data-testid="share-create-btn" className="w-full flex items-center justify-center gap-2 bg-[#17A2B8]/15 hover:bg-[#17A2B8]/25 border border-[#17A2B8]/40 text-[#2ec5db] font-semibold py-2 rounded-md text-xs transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Créer un lien public (24 h)
              </button>
              {share && (
                <div className="mt-2 flex items-center gap-2" data-testid="share-link-box">
                  <input readOnly value={share} data-testid="share-link-input" onFocus={(e) => e.target.select()} className="flex-1 bg-secondary/40 border border-border rounded px-2 py-1.5 text-[11px] data" />
                  <button onClick={() => { navigator.clipboard.writeText(share); toast.success("Copié"); }} data-testid="share-copy-btn" className="text-muted-foreground hover:text-foreground p-1.5"><Copy className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
            </div>)}

            {/* ===== ONGLET CAN ===== */}
            {drawerTab === "can" && (<div data-testid="tab-can">
            {selected.last_position?.can && Object.keys(selected.last_position.can).length > 0 ? (() => {
              const avail = canEntries(selected.last_position.can);
              const disp = selected.can_display;
              const isOn = (k) => !disp || disp.length === 0 || disp.includes(k);
              const shown = avail.filter((f) => isOn(f.key));
              return (
                <div className="mt-1" data-testid="detail-can">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="label-mono flex items-center gap-1"><Zap className="w-3.5 h-3.5 brand-teal" /> Données CAN (bus véhicule)</p>
                    <button onClick={() => setCanSettings((s) => !s)} data-testid="can-settings-btn" title="Choisir les données à afficher"
                      className="text-muted-foreground hover:text-foreground p-0.5"><Settings className="w-3.5 h-3.5" /></button>
                  </div>
                  {canSettings && (
                    <div className="mb-2 border border-border rounded-md bg-secondary/40 p-2 space-y-1" data-testid="can-settings">
                      <p className="text-[10px] text-muted-foreground mb-1">Cochez les données CAN à afficher en Live Tracking :</p>
                      {avail.map((f) => (
                        <label key={f.key} data-testid={`can-toggle-${f.key}`} className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" checked={isOn(f.key)} onChange={() => toggleCanField(f.key)} data-testid={`can-check-${f.key}`} />
                          <f.Icon className="w-3.5 h-3.5 brand-teal" /> {f.label}
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 data text-xs">
                    {shown.map((f) => (
                      <div key={f.key} className="flex items-center gap-1.5" data-testid={`can-field-${f.key}`}>
                        <f.Icon className="w-3.5 h-3.5 brand-teal shrink-0" />
                        <span className="text-muted-foreground">{f.label}</span>
                        <span className="font-semibold ml-auto">{f.value}{f.unit ? ` ${f.unit}` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })() : (
              <p className="text-xs text-muted-foreground py-8 text-center" data-testid="can-empty">Aucune donnée CAN disponible pour ce véhicule.</p>
            )}
            {(selected.dtc_count > 0 || (selected.dtc_codes && selected.dtc_codes.length > 0)) && (
              <div className="mt-3 rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 p-2.5" data-testid="detail-dtc-can">
                <p className="label-mono flex items-center gap-1 text-[#EF4444] mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Défauts moteur (DTC) · {selected.dtc_count || (selected.dtc_codes || []).length}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.dtc_codes || []).length > 0 ? (selected.dtc_codes.map((c) => (
                    <span key={c} className="font-mono text-[11px] font-bold text-[#EF4444] bg-[#EF4444]/15 border border-[#EF4444]/40 rounded px-1.5 py-0.5">{c}</span>
                  ))) : (<span className="text-[11px] text-muted-foreground">Nombre de défauts remonté par le boîtier (codes non décodés).</span>)}
                </div>
              </div>
            )}
            </div>)}

            {/* ===== ONGLET TRAJETS ===== */}
            {drawerTab === "trajets" && (<div className="mt-1" data-testid="tab-trajets">
              <div className="flex items-center justify-between mb-2">
                <p className="label-mono">Trajets du jour</p>
                <span className="data text-[11px] text-muted-foreground">{dayTrips ? `${dayTrips.count} · ${dayTrips.total_km} km` : "…"}</span>
              </div>
              <button type="button" onClick={() => setShowTrips(true)} data-testid="trajets-open-modal" className="w-full mb-2 text-xs font-semibold border border-border rounded-md py-1.5 hover:border-[#17A2B8] hover:text-[#17A2B8]">Ouvrir le détail des trajets</button>
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-0.5">
                {!dayTrips && <p className="text-xs text-muted-foreground py-2 text-center">Chargement des trajets…</p>}
                {dayTrips && dayTrips.trips.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">Aucun trajet aujourd'hui.</p>}
                {dayTrips && dayTrips.trips.map((t, i) => {
                  const st = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`);
                  const fmt = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { timeZone: "Pacific/Noumea", hour: "2-digit", minute: "2-digit" });
                  return (
                    <div key={i}>
                      {t.gap_before_min != null && t.gap_before_min > 0 && (
                        <p className="flex items-center gap-1 text-[10px] text-muted-foreground my-1 pl-1" data-testid={`trip-gap-${i}`}>
                          <Clock className="w-3 h-3" /> Arrêt de {st(t.gap_before_min)} avant ce trajet
                        </p>
                      )}
                      <button type="button" onClick={() => selectTrip(t)} data-testid={`detail-trip-${i}`}
                        className="w-full text-left border border-border rounded-md p-2 bg-secondary/40 hover:border-[#17A2B8] hover:bg-[#17A2B8]/10 transition-colors">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="flex items-center gap-1.5 font-bold text-xs"><Route className="w-3.5 h-3.5 brand-teal" /> Trajet {i + 1}</span>
                          <span className="data text-xs font-bold brand-orange">{t.distance_km} km · {st(t.duration_min)}</span>
                        </div>
                        <div className="space-y-1 data text-[11px]">
                          <div className="flex items-center gap-1.5">
                            <Flag className="w-3.5 h-3.5 text-[#10B981] shrink-0" title="Départ" />
                            <span className="font-semibold shrink-0">{fmt(t.start)}</span>
                            <span className="text-muted-foreground truncate">· {tripAddr[i] === undefined ? "localisation…" : (tripAddr[i].start || "Adresse indisponible")}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Flag className="w-3.5 h-3.5 text-[#EF4444] shrink-0" title="Arrivée" />
                            <span className="font-semibold shrink-0">{fmt(t.end)}</span>
                            <span className="text-muted-foreground truncate">· {tripAddr[i] === undefined ? "localisation…" : (tripAddr[i].end || "Adresse indisponible")}</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>)}

            {/* ===== ONGLET ÉCO ===== */}
            {drawerTab === "eco" && (<div className="mt-1" data-testid="tab-eco">
              <div className="flex items-center justify-between mb-1.5">
                <p className="label-mono flex items-center gap-1"><Leaf className="w-3.5 h-3.5 text-[#10B981]" /> Éco-conduite</p>
                <button onClick={toggleEco} data-testid="eco-toggle-btn" title={ecoSummary?.enabled ? "Désactiver l'éco-conduite" : "Activer l'éco-conduite"}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${ecoSummary?.enabled ? "bg-[#10B981]" : "bg-secondary border border-border"}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${ecoSummary?.enabled ? "translate-x-4" : "translate-x-1"}`} />
                </button>
              </div>
              {!ecoSummary && <p className="text-xs text-muted-foreground py-4 text-center">Chargement…</p>}
              {ecoSummary?.enabled ? (
                <div data-testid="eco-summary">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Score (7 j)</span>
                    <span className="data font-bold" style={{ color: ecoColor(ecoSummary.score) }}>{ecoSummary.score == null ? "—" : `${ecoSummary.score}/100`}</span>
                  </div>
                  <div className="h-2 rounded-full bg-black/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${ecoSummary.score || 0}%`, background: ecoColor(ecoSummary.score) }} /></div>
                  <div className="grid grid-cols-2 gap-2 mt-3 data text-xs">
                    <Detail label="Accélérations" value={ecoSummary.counts?.harsh_accel ?? 0} />
                    <Detail label="Freinages" value={ecoSummary.counts?.harsh_brake ?? 0} />
                    <Detail label="Virages" value={ecoSummary.counts?.cornering ?? 0} />
                    <Detail label="Excès de vitesse" value={ecoSummary.counts?.overspeed ?? 0} />
                    {ecoSummary.counts?.idle != null && <Detail label="Ralenti" value={ecoSummary.counts.idle} />}
                    {ecoSummary.km_analyzed != null && <Detail label="Km analysés" value={`${ecoSummary.km_analyzed} km`} />}
                  </div>
                </div>
              ) : ecoSummary && (
                <p className="text-[11px] text-muted-foreground">Activez pour suivre accélérations, freinages, virages et excès de vitesse.</p>
              )}
            </div>)}

            {/* ===== ONGLET MAINTENANCE ===== */}
            {drawerTab === "maintenance" && (<div className="mt-1 space-y-3" data-testid="tab-maintenance">
              {(() => {
                const isH = selected.usage_metric === "engine_hours";
                const eff = isH ? (selected.engine_hours || 0) : (selected.odometer_km || 0);
                const raw = isH ? selected.engine_hours_raw : selected.odometer_raw_km;
                const src = isH ? selected.engine_hours_source : selected.odometer_source;
                const factor = isH ? selected.eh_factor : selected.odo_factor;
                const mode = isH ? selected.engine_hours_calibration_mode : selected.odometer_calibration_mode;
                const SRC = { gps: "GPS", gps_calibrated: "GPS ajusté", can: "CAN", manual: "Manuel", ignition: "Contact", rpm: "Régime moteur", calibrated: "Contact ajusté", device: "Boîtier" };
                const fmt = (n) => isH ? `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h` : `${Math.round(n || 0).toLocaleString("fr-FR")} km`;
                const interval = isH ? (selected.maintenance_interval_hours || 250) : (selected.maintenance_interval_km || 10000);
                const canMeter = isAdmin || user?.role === "gestionnaire";
                const nextKm = selected.next_maintenance_km != null ? selected.next_maintenance_km : ((selected.maintenance_interval_km || 10000) - (Math.round(selected.odometer_km || 0) % (selected.maintenance_interval_km || 10000)));
                return (<>
                  <div className="rounded-md border border-border p-3 space-y-2" data-testid="meter-panel">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[11px] text-muted-foreground">{isH ? "Heures moteur" : "Kilométrage"}</p>
                        <p className="data text-lg font-bold" data-testid="meter-effective">{fmt(eff)}</p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#17A2B8]/15 text-[#17A2B8]" data-testid="meter-source">{SRC[src] || "GPS"}</span>
                    </div>
                    {isAdmin && <p className="text-[10px] text-muted-foreground data" data-testid="meter-admin-detail">RAW {fmt(raw != null ? raw : eff)} · mode {mode || "off"}{factor && factor !== 1 ? ` · ×${factor}` : ""}</p>}
                    {canMeter && meterForm === null && (
                      <button onClick={() => setMeterForm({ value: "" })} data-testid="meter-read-btn" className="text-xs font-bold text-[#17A2B8] hover:underline">{isH ? "Relever le compteur horaire" : "Relever le compteur"}</button>
                    )}
                    {canMeter && meterForm !== null && (
                      <div className="flex items-center gap-2">
                        <input type="number" step={isH ? "0.1" : "1"} autoFocus value={meterForm.value} data-testid="meter-input"
                          onChange={(e) => setMeterForm({ value: e.target.value })} placeholder={isH ? "Heures réelles" : "Km réels"}
                          className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm w-32 outline-none focus:border-[#17A2B8]" />
                        <button disabled={meterBusy || meterForm.value === ""} data-testid="meter-save-btn" onClick={submitMeterReading} className="text-xs font-bold bg-[#17A2B8] text-white px-3 py-1.5 rounded-md disabled:opacity-40">Enregistrer</button>
                        <button onClick={() => setMeterForm(null)} data-testid="meter-cancel-btn" className="text-xs text-muted-foreground">Annuler</button>
                      </div>
                    )}
                    {pendingFactor && pendingFactor.metric === (isH ? "engine_hours" : "odometer") && (
                      <div className="rounded-md border border-[#F5821F]/40 bg-[#F5821F]/10 p-2 text-xs" data-testid="meter-factor-banner">
                        Écart moyen détecté : {Math.round((pendingFactor.factor - 1) * 100)} %. Appliquer aux futurs {isH ? "heures" : "kilomètres"} ?
                        <div className="flex gap-2 mt-1">
                          <button onClick={acceptFactor} data-testid="meter-factor-accept" className="font-bold text-[#10B981]">Appliquer</button>
                          <button onClick={() => setPendingFactor(null)} data-testid="meter-factor-refuse" className="text-muted-foreground">Ignorer</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 data text-xs">
                    <Detail label="Intervalle" value={isH ? `${interval.toLocaleString("fr-FR")} h` : `${interval.toLocaleString("fr-FR")} km`} />
                    <Detail label="Prochaine maintenance" value={isH ? (selected.next_maintenance_hours != null ? `${Number(selected.next_maintenance_hours).toLocaleString("fr-FR")} h` : "—") : `${nextKm.toLocaleString("fr-FR")} km`} icon={Wrench} />
                  </div>
                  {selected.next_maintenance_at && <Detail label="Échéance temporelle" value={fmtTime(selected.next_maintenance_at)} />}
                </>);
              })()}
              <div className="rounded-md border border-border p-3" data-testid="maint-schedules">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-sm">Entretiens programmés</p>
                  {(isAdmin || user?.role === "gestionnaire") && maintForm === null && (
                    <button onClick={() => setMaintForm({ label: "", due: "", notice: "", recur: "" })} data-testid="maint-add-btn" className="text-xs font-bold text-[#17A2B8] hover:underline">+ Ajouter</button>
                  )}
                </div>
                {maintForm !== null && (() => { const isH = selected.usage_metric === "engine_hours"; const u = isH ? "h" : "km"; return (
                  <div className="space-y-2 mb-3 p-2 rounded bg-secondary/40" data-testid="maint-form">
                    <input value={maintForm.label} onChange={(e) => setMaintForm({ ...maintForm, label: e.target.value })} placeholder="Libellé (Vidange, Révision…)" data-testid="maint-label" className="w-full bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8]" />
                    <div className="grid grid-cols-3 gap-2">
                      <input type="number" value={maintForm.due} onChange={(e) => setMaintForm({ ...maintForm, due: e.target.value })} placeholder={`Échéance (${u})`} data-testid="maint-due" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs outline-none focus:border-[#17A2B8]" />
                      <input type="number" value={maintForm.notice} onChange={(e) => setMaintForm({ ...maintForm, notice: e.target.value })} placeholder={`Préavis (${u})`} data-testid="maint-notice" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs outline-none focus:border-[#17A2B8]" />
                      <input type="number" value={maintForm.recur} onChange={(e) => setMaintForm({ ...maintForm, recur: e.target.value })} placeholder={`Récurrence (${u})`} data-testid="maint-recur" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs outline-none focus:border-[#17A2B8]" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={addMaint} data-testid="maint-save" className="text-xs font-bold bg-[#17A2B8] text-white px-3 py-1.5 rounded-md">Enregistrer</button>
                      <button onClick={() => setMaintForm(null)} className="text-xs text-muted-foreground">Annuler</button>
                    </div>
                  </div>
                ); })()}
                {maintList.filter((m) => m.status === "planifie").length === 0 && <p className="text-xs text-muted-foreground" data-testid="maint-empty">Aucune échéance — la maintenance est opt-in.</p>}
                {maintList.filter((m) => m.status === "planifie").map((m) => {
                  const isH = m.usage_metric === "engine_hours"; const u = isH ? "h" : "km";
                  const due = isH ? m.due_hours : m.due_km; const cur = m.current_value;
                  const notice = (isH ? m.notice_hours : m.notice_km) || 0;
                  const rem = due != null ? Math.round((due - cur) * 10) / 10 : null;
                  const state = due == null ? "date" : (cur > due ? "overdue" : (cur >= due ? "due" : (cur >= due - notice ? "soon" : "planned")));
                  const badge = { overdue: ["En retard", "text-[#EF4444] bg-[#EF4444]/15"], due: ["À échéance", "text-[#EF4444] bg-[#EF4444]/15"], soon: ["À prévoir", "text-[#F5821F] bg-[#F5821F]/15"], planned: ["Planifié", "text-muted-foreground bg-secondary"], date: ["Calendaire", "text-muted-foreground bg-secondary"] }[state];
                  return (
                    <div key={m.id} className="flex items-center gap-2 py-2 border-t border-border first:border-t-0" data-testid={`maint-row-${m.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{m.label || m.m_type}</p>
                        <p className="text-[11px] text-muted-foreground data">{due != null ? `Prévu à ${Number(due).toLocaleString("fr-FR")} ${u} · ${rem >= 0 ? `restant ${rem}` : `retard ${Math.abs(rem)}`} ${u}` : (m.due_date ? `le ${m.due_date.slice(0, 10)}` : "")}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 ${badge[1]}`} data-testid={`maint-state-${m.id}`}>{badge[0]}</span>
                      {(isAdmin || user?.role === "gestionnaire") && (
                        <>
                          <button onClick={() => doneMaint(m.id)} data-testid={`maint-done-${m.id}`} title="Marquer comme effectuée" className="p-1.5 text-muted-foreground hover:text-[#10B981]"><Wrench className="w-4 h-4" /></button>
                          <button onClick={() => delMaint(m.id)} className="p-1.5 text-muted-foreground hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
                <div className="rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 p-2.5" data-testid="detail-dtc">
                  <div className="flex items-center justify-between mb-1">
                    <p className="label-mono flex items-center gap-1 text-[#EF4444]"><AlertTriangle className="w-3.5 h-3.5" /> Défauts moteur (DTC) · {selected.dtc_count || (selected.dtc_codes || []).length}</p>
                    {isAdmin && (
                      <button onClick={async () => { try { await api.post(`/vehicles/${selected.id}/dtc-clear`); toast.success("Défauts effacés"); load(); } catch { toast.error("Erreur"); } }} data-testid="dtc-clear-btn" className="text-[10px] font-bold text-muted-foreground hover:text-[#EF4444] underline">Effacer</button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.dtc_codes || []).length > 0 ? (selected.dtc_codes.map((c) => (
                      <span key={c} data-testid={`dtc-code-${c}`} className="font-mono text-[11px] font-bold text-[#EF4444] bg-[#EF4444]/15 border border-[#EF4444]/40 rounded px-1.5 py-0.5">{c}</span>
                    ))) : (<span className="text-[11px] text-muted-foreground">Nombre de défauts remonté par le boîtier (codes non décodés).</span>)}
                  </div>
                  {selected.dtc_at && <p className="text-[10px] text-muted-foreground mt-1.5">Détecté {timeAgo(selected.dtc_at)}</p>}
                </div>
            </div>)}
          </VehicleDrawer>
        )}
        {playback && cur && (
          <div className="absolute bottom-20 lg:bottom-5 left-3 right-3 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 w-auto lg:w-[640px] lg:max-w-[calc(100%-380px)] glass rounded-lg p-4 z-[600] animate-slidein" data-testid="playback-bar">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 brand-orange" />
                <span className="font-bold text-sm">{selected?.name}</span>
                <span className="label-mono">· lecture de trajet</span>
              </div>
              <button onClick={() => setPlayback(null)} data-testid="playback-close" className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"><Square className="w-3.5 h-3.5" /> Fermer</button>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => setPlayback((p) => ({ ...p, idx: 0, playing: true }))} data-testid="playback-restart" className="text-muted-foreground hover:text-foreground p-2 rounded hover:bg-black/5"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={() => setPlayback((p) => ({ ...p, playing: !p.playing }))} data-testid="playback-toggle" className="bg-[#F5821F] hover:bg-[#e0741a] text-white p-2.5 rounded-full transition-colors">
                {playback.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>

              <input
                type="range" min={0} max={playback.track.length - 1} value={playback.idx}
                data-testid="playback-slider"
                onChange={(e) => setPlayback((p) => ({ ...p, idx: Number(e.target.value), playing: false }))}
                className="flex-1 accent-[#F5821F] cursor-pointer"
              />

              <button
                onClick={() => setPlayback((p) => ({ ...p, speed: SPEEDS[(SPEEDS.indexOf(p.speed) + 1) % SPEEDS.length] }))}
                data-testid="playback-speed"
                className="data text-xs font-bold border border-border rounded px-2.5 py-1.5 hover:border-[#F5821F] min-w-[44px]"
              >
                {playback.speed}×
              </button>
            </div>

            <div className="flex items-center justify-between mt-3 data text-xs text-muted-foreground">
              <span>{fmtTime(cur.timestamp)}</span>
              <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5 brand-orange" /> {cur.speed} km/h · cap {cur.angle}°</span>
              <span>{playback.idx + 1} / {playback.track.length} points</span>
            </div>
          </div>
        )}

        {selected && showInvoices && <InvoiceModal vehicle={selected} onClose={() => setShowInvoices(false)} />}
        {selected && showControl && <UnitControlModal vehicle={selected} onClose={() => setShowControl(false)} />}
        {cutDialog && selected && <EngineCutDialog vehicle={selected} action={cutDialog} onConfirm={sendCut} onClose={() => setCutDialog(null)} />}
        {selected && showTrips && <TripsModal vehicle={selected} onClose={() => setShowTrips(false)} onSelectTrip={selectTrip} />}
        {sensorChart && <SensorChartModal vehicle={sensorChart.vehicle} metric={sensorChart.metric} onClose={() => setSensorChart(null)} />}
        {fuelChart && <FuelChartModal vehicle={fuelChart.vehicle} onClose={() => setFuelChart(null)} />}
        {showHistory && historyVehicle && <HistoryModal vehicle={historyVehicle} onClose={() => setShowHistory(false)} onSelectTrip={(t) => { setSelected(historyVehicle); selectedId.current = historyVehicle.id; selectTrip(t); setShowHistory(false); }} />}

        {showRouteTool && (
          <RouteToolPanel pois={visiblePois} selected={selected} onRoute={applyRoute} onClose={() => setShowRouteTool(false)} />
        )}
        {vehicles.some((v) => v.sos_active) && (
          <div className="absolute top-14 left-3 right-3 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 lg:w-[520px] z-[700] flex flex-col gap-1.5 animate-slidein" data-testid="sos-banner">
            {vehicles.filter((v) => v.sos_active).map((v) => (
              <div key={v.id} className="flex items-center gap-2 bg-[#DC2626] text-white rounded-xl px-3 py-2 shadow-lg border border-white/20" data-testid={`sos-banner-${v.id}`}>
                <Siren className="w-5 h-5 shrink-0 animate-pulse" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate">SOS — {v.name} <span className="font-normal opacity-90">· {v.plate}</span></p>
                  <p className="text-[10px] opacity-90 data truncate">{v.sos_at ? new Date(v.sos_at).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}{v.sos_lat != null ? <> · <AddressLabel lat={v.sos_lat} lon={v.sos_lon} testid={`sos-address-${v.id}`} /></> : ""}</p>
                </div>
                <button onClick={() => selectVehicle(v)} data-testid={`sos-goto-${v.id}`} className="shrink-0 text-[11px] font-bold bg-white/20 hover:bg-white/30 rounded-md px-2 py-1 transition-colors">Voir</button>
                <button onClick={() => ackSos(v.id)} data-testid={`sos-ack-${v.id}`} className="shrink-0 text-[11px] font-bold bg-white text-[#DC2626] hover:bg-white/90 rounded-md px-2 py-1 transition-colors">Acquitter</button>
              </div>
            ))}
          </div>
        )}
        {routePath.length > 1 && routeInfo && (
          <div className={`absolute ${sosBannerTop} left-3 right-3 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 z-[560] glass rounded-xl lg:rounded-full px-3 py-2 flex flex-wrap justify-center lg:flex-nowrap lg:w-auto items-center gap-x-3 gap-y-1.5 animate-slidein`} data-testid="route-banner">
            <span className="text-xs font-semibold flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 brand-orange" /> Itinéraire · {routeInfo.distance_km} km · ~{routeInfo.duration_min} min</span>
            {routeDest?.label && <span className="text-[11px] text-muted-foreground truncate max-w-[200px]">→ {routeDest.label}</span>}
            <button onClick={clearRoute} data-testid="route-clear-btn" className="text-xs font-semibold text-[#EF4444] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Effacer</button>
          </div>
        )}

        {tripPath.length > 1 && (
          <div className={`absolute ${sosBannerTop} left-3 right-3 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 z-[560] glass rounded-xl lg:rounded-full px-3 py-2 flex flex-wrap justify-center lg:flex-nowrap lg:w-auto items-center gap-x-3 gap-y-1.5 animate-slidein`} data-testid="trip-banner">
            <span className="text-xs font-semibold flex items-center gap-1.5"><Route className="w-3.5 h-3.5 brand-teal" /> Trajet · {tripPath.length} pts</span>
            <button onClick={replayTrip} data-testid="trip-replay-btn" className="text-xs font-semibold text-[#F5821F] flex items-center gap-1"><History className="w-3.5 h-3.5" /> Rejouer</button>
            <button onClick={exportGpx} data-testid="trip-gpx-btn" className="text-xs font-semibold text-[#2ec5db] flex items-center gap-1"><Download className="w-3.5 h-3.5" /> GPX</button>
            <button onClick={exportPdf} data-testid="trip-pdf-btn" className="text-xs font-semibold text-[#2ec5db] flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> PDF</button>
            <button onClick={() => { setTripPath([]); setTripInfo(null); setFicheOpenMobile(false); }} data-testid="trip-clear-btn" className="text-xs font-semibold text-[#EF4444] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Effacer</button>
          </div>
        )}

        {selected && tripPath.length > 1 && !ficheOpenMobile && (
          <button onClick={() => setFicheOpenMobile(true)} data-testid="mobile-open-fiche"
            className="lg:hidden absolute bottom-20 left-1/2 -translate-x-1/2 z-[560] glass rounded-full px-4 py-2 flex items-center gap-2 text-xs font-bold animate-slidein">
            <Info className="w-4 h-4 brand-teal" /> Fiche véhicule
          </button>
        )}

        {sensorTip && (
          <div className="fixed z-[900] w-56 rounded-lg p-3 shadow-xl pointer-events-none animate-slidein text-white"
            style={{ left: sensorTip.x, top: sensorTip.y + 6, background: "rgba(10,10,12,0.72)", backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.12)" }}
            data-testid="sensor-tip">
            <p className="text-[9px] uppercase tracking-wider text-white/60 mb-1">{sensorTip.label}</p>
            <p className="data text-sm font-bold mb-1.5">{sensorTip.value}</p>
            <p className="flex items-center gap-1 pt-1.5 border-t border-white/10 text-[10px] text-white/80"><History className="w-3 h-3 shrink-0" /> Dernière donnée : {sensorTip.ts ? fmtSensorDT(sensorTip.ts) : "—"}</p>
          </div>
        )}

        {hoverInfo && (() => {
          const v = hoverInfo.v; const lp = v.last_position || {}; const st = businessStatus(v);
          const VIcon = getVehicleIcon(v.icon);
          const extV = lp.external_voltage != null ? (lp.external_voltage / 1000).toFixed(1) : null;
          const bp = battPct(lp.battery_voltage);
          return (
            <div className="fixed z-[900] w-64 glass rounded-lg p-3 shadow-xl pointer-events-none animate-slidein" style={{ left: hoverInfo.x, top: hoverInfo.y + 6 }} data-testid={`vehicle-info-pop-${(v.plate || v.id).replace(/\s/g, "")}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-sm flex items-center gap-1.5"><VIcon className="w-4 h-4" style={{ color: st.color }} />{v.name}</span>
                <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color: st.color }}><span className="w-2 h-2 rounded-full" style={{ background: st.color }} />{st.label}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {extV != null && v.sensors?.ext_voltage?.enabled !== false && <Sensor icon={Zap} value={`${extV}v`} color={extV < 11.5 ? "#EF4444" : "#00A0B0"} title={lp.timestamp ? `Dernière donnée : ${fmtTime(lp.timestamp)} · ${timeAgo(lp.timestamp)}` : "Aucune donnée"} />}
                {bp != null && v.sensors?.battery?.enabled !== false && <Sensor icon={BatteryMedium} value={`${bp}%`} color={bp < 20 ? "#EF4444" : bp < 50 ? "#F59E0B" : "#10B981"} title={lp.timestamp ? `Dernière donnée : ${fmtTime(lp.timestamp)} · ${timeAgo(lp.timestamp)}` : "Aucune donnée"} />}
                {lp.satellites != null && v.sensors?.satellites?.enabled !== false && <Sensor icon={Satellite} value={lp.satellites} color={lp.satellites < 4 ? "#F59E0B" : "#10B981"} title={lp.timestamp ? `Dernière donnée : ${fmtTime(lp.timestamp)} · ${timeAgo(lp.timestamp)}` : "Aucune donnée"} />}
                {lp.gsm_signal != null && v.sensors?.gsm?.enabled !== false && <Sensor icon={SignalHigh} value={`${lp.gsm_signal}/5`} color={lp.gsm_signal >= 3 ? "#10B981" : "#F59E0B"} title={lp.timestamp ? `Dernière donnée : ${fmtTime(lp.timestamp)} · ${timeAgo(lp.timestamp)}` : "Aucune donnée"} />}
              </div>
              <div className="space-y-1 data text-[11px] text-muted-foreground">
                <p className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 brand-orange" /> {st.fresh ? fmtSpeed(lp.speed, isMarine(v)) : "—"} · cap {lp.angle ?? 0}°{!st.fresh && <span className="text-[10px]">· dernier état connu</span>}</p>
                {(v.current_driver_name || v.driver || "").trim() && <p className="flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5 brand-teal" /> {(v.current_driver_name || v.driver).trim()}</p>}
                <p className="flex items-center gap-1.5"><Route className="w-3.5 h-3.5 brand-teal" /> {usageText(v)}</p>
                <p className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 brand-orange shrink-0" /> <span className="truncate">{locLabel(v) === undefined ? "Localisation…" : (locLabel(v) || "Adresse indisponible")}</span></p>
                <p className="flex items-center gap-1.5"><History className="w-3.5 h-3.5 brand-teal" /> {lp.timestamp ? fmtTime(lp.timestamp) : "—"}</p>
              </div>
            </div>
          );
        })()}

        {selected?.cut_confirmed && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[560] glass rounded-full px-4 py-2 flex items-center gap-2 animate-slidein border border-[#10B981]/50" data-testid="cut-confirmed-banner">
            <Power className="w-4 h-4 text-[#10B981]" />
            <span className="text-xs font-semibold text-[#10B981]">Coupure moteur confirmée par le boîtier · {selected.name}</span>
          </div>
        )}

        {showDevices && <DevicesStatusModal onClose={() => setShowDevices(false)} />}

        {/* Alerts ticker */}
        <div className={`absolute top-3 lg:top-3 left-3 right-3 lg:left-auto lg:right-5 w-auto lg:w-[320px] max-h-[70vh] lg:max-h-none glass rounded-lg z-[550] animate-slidein ${mobilePanel === "alerts" ? "flex flex-col" : "hidden"} lg:block ${followFull ? "!hidden lg:!hidden" : ""}`} data-testid="alerts-ticker">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <Bell className="w-4 h-4 brand-orange" />
            <p className="font-bold text-sm">Derniers événements{followId ? " · suivi" : ""}</p>
            <span className="ml-auto data text-xs bg-[#F5821F]/20 text-[#F5821F] px-2 py-0.5 rounded">{shownAlerts.length}</span>
            <button onClick={() => setAlertsCollapsed((c) => !c)} data-testid="alerts-collapse-btn" title={alertsCollapsed ? "Déplier" : "Réduire"} className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-black/5 transition-colors">
              {alertsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
          {!alertsCollapsed && (
          <div className="max-h-[300px] overflow-y-auto p-2 space-y-1.5">
            {shownAlerts.length === 0 && <p className="text-center text-xs text-muted-foreground py-6">Aucun événement récent</p>}
            {shownAlerts.map((a) => {
              const m = alertMeta(a.type);
              return (
                <div key={a.id} className="flex items-start gap-2.5 p-2 rounded-md hover:bg-black/5">
                  <m.icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: m.color }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate">{a.vehicle}</p>
                    <p className="text-[11px] text-muted-foreground">{a.message}</p>
                    <p className="data text-[10px] text-muted-foreground mt-0.5">{timeAgo(a.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const Detail = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-semibold text-foreground flex items-center gap-1">{Icon && <Icon className="w-3 h-3 brand-teal" />}{value}</p>
  </div>
);

